import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MenuIcon, PhoneIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { CONTACT } from '../data/media';
import { Mascot } from './Mascot';
export function Navbar() {
  const {
    t,
    lang,
    setLang
  } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);
  const links = [{
    href: '#services',
    label: t.nav.services
  }, {
    href: '#projects',
    label: t.nav.gallery
  }, {
    href: '#about',
    label: t.nav.about
  }, {
    href: '#process',
    label: t.nav.process
  }, {
    href: '#contact',
    label: t.nav.contact
  }];
  return <header className={`fixed inset-x-0 top-0 z-50 border-b border-brand-500/60 bg-ink/95 backdrop-blur transition-shadow duration-200 ease-smooth ${scrolled ? 'shadow-[0_10px_30px_-20px_rgba(0,0,0,0.9)]' : ''}`} data-id="element-199" data-mp="emrhe21kcufr">
      <div className="mx-auto flex h-[72px] max-w-shell items-center gap-4 px-4 sm:px-6 lg:px-8" data-id="element-200" data-mp="okyu28otkqby">
        <a href="#top" className="flex items-center gap-3" aria-label={CONTACT.company} data-id="element-201" data-mp="3n57349vbc9bv">
          <Mascot size={40} tone="dark" animate data-id="element-202" data-mp="3s2ytgzgb7ndy" />
          <span className="font-display text-[13px] font-extrabold uppercase leading-tight tracking-wide text-white sm:text-sm" data-id="element-203" data-mp="wfmjvdlghkx">
            Pollo Construction
            <span className="block text-[10px] font-bold tracking-[0.22em] text-brand-500 sm:text-[11px]" data-id="element-204" data-mp="5o76vo65pr2m">
              &amp; Granite Inc.
            </span>
          </span>
        </a>

        <nav aria-label="Main" className="ml-auto hidden items-center gap-7 lg:flex" data-id="element-205" data-mp="39aeiix0kg2yf">
          {links.map(link => <a key={link.href} href={link.href} className="text-sm font-semibold text-white/80 transition-colors duration-200 ease-smooth hover:text-brand-500" data-id="element-206" data-mp="3196wnbatg4n8">
              {link.label}
            </a>)}
        </nav>

        <div className="ml-auto flex items-center gap-2 lg:ml-6" data-id="element-207" data-mp="362rth00oasku">
          <div className="hidden overflow-hidden rounded border border-white/15 sm:flex" role="group" aria-label={t.nav.language} data-id="element-208" data-mp="2idgqrczrio42">
            {(['en', 'es'] as const).map(code => <button key={code} type="button" onClick={() => setLang(code)} aria-pressed={lang === code} className={`px-2.5 py-1.5 font-display text-[11px] font-bold uppercase tracking-wider transition-colors duration-200 ease-smooth ${lang === code ? 'bg-brand-500 text-ink' : 'text-white/70 hover:bg-white/10 hover:text-white'}`} data-id="element-209" data-mp="2sbwfj9bbhlae">
                {code}
              </button>)}
          </div>

          <a href={`tel:+1${CONTACT.phoneRaw}`} className="hidden items-center gap-2 rounded border border-white/20 px-3 py-2 text-sm font-bold text-white transition-colors duration-200 ease-smooth hover:border-brand-500 hover:text-brand-500 md:inline-flex" data-id="element-210" data-mp="15otch17y3p2">
            <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-211" data-mp="xt1wahe1gws2" />
            {CONTACT.phoneDisplay}
          </a>

          <a href="#contact" className="hidden rounded bg-brand-500 px-4 py-2.5 font-display text-[13px] font-extrabold uppercase tracking-wide text-ink transition-colors duration-200 ease-smooth hover:bg-brand-400 sm:inline-block" data-id="element-212" data-mp="k2q0a7ec2dvn">
            {t.nav.quote}
          </a>

          <button type="button" onClick={() => setOpen(true)} className="inline-flex h-11 w-11 items-center justify-center rounded border border-white/20 text-white lg:hidden" aria-label={t.nav.menu} data-id="element-213" data-mp="1gmz1dh15unb5">
            <MenuIcon className="h-5 w-5" aria-hidden="true" data-id="element-214" data-mp="3f4sph1gvwor9" />
          </button>
        </div>
      </div>

      <AnimatePresence data-id="element-215" data-mp="3b37drjg36gin">
        {open ? <motion.div className="fixed inset-0 z-50 bg-ink lg:hidden" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-216" data-mp="3b4il1t8qq6ei">
            <div className="flex h-[72px] items-center justify-between border-b border-white/10 px-4 sm:px-6" data-id="element-217" data-mp="1vo76zwllmp5">
              <span className="font-display text-sm font-extrabold uppercase text-white" data-id="element-218" data-mp="3tyxkfjyys12v">
                {CONTACT.company}
              </span>
              <button type="button" onClick={() => setOpen(false)} className="inline-flex h-11 w-11 items-center justify-center rounded border border-white/20 text-white" aria-label={t.nav.close} data-id="element-219" data-mp="pma5w0kppumh">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-220" data-mp="2ha88q8hlpm05" />
              </button>
            </div>
            <nav aria-label="Mobile" className="flex flex-col px-4 py-6 sm:px-6" data-id="element-221" data-mp="2m9b9vhugn82u">
              {links.map(link => <a key={link.href} href={link.href} onClick={() => setOpen(false)} className="border-b border-white/10 py-4 font-display text-lg font-bold text-white transition-colors duration-200 ease-smooth hover:text-brand-500" data-id="element-222" data-mp="2r97ixvi0lpp9">
                  {link.label}
                </a>)}
              <div className="mt-6 flex gap-2" role="group" aria-label={t.nav.language} data-id="element-223" data-mp="39zfgzz6uxpyl">
                {(['en', 'es'] as const).map(code => <button key={code} type="button" onClick={() => setLang(code)} aria-pressed={lang === code} className={`flex-1 rounded border py-3 font-display text-sm font-bold uppercase ${lang === code ? 'border-brand-500 bg-brand-500 text-ink' : 'border-white/20 text-white/80'}`} data-id="element-224" data-mp="3ey9xguhcr2fj">
                    {code === 'en' ? 'English' : 'Español'}
                  </button>)}
              </div>
              <a href={`tel:+1${CONTACT.phoneRaw}`} className="mt-4 inline-flex items-center justify-center gap-2 rounded border border-white/20 py-4 font-display text-base font-bold text-white" data-id="element-225" data-mp="1tmzq818kmu89">
                <PhoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-226" data-mp="d5luk0vkhtmj" />
                {CONTACT.phoneDisplay}
              </a>
              <a href="#contact" onClick={() => setOpen(false)} className="mt-3 rounded bg-brand-500 py-4 text-center font-display text-base font-extrabold uppercase tracking-wide text-ink" data-id="element-227" data-mp="88vdpbbxuu02">
                {t.nav.quote}
              </a>
            </nav>
          </motion.div> : null}
      </AnimatePresence>
    </header>;
}